import React, { useContext, useState } from "react";
import { IoSettingsOutline } from "react-icons/io5";
import { FiPlus } from "react-icons/fi";
import CustomSkeleton from "../components/CustomSkeleton";
import AddMembersToOrganization from "../components/modals/AddMembersToOrganization";
import { OrganizationContext } from "../contexts/OrganizationContextProvider";
import CustomButton from "../components/CustomButton";
import DangerCard from "../components/ui/DangerCard";
import ModalUI from "../components/ui/ModalUI";
import {
  deleteOrganization,
  removeMemberFromOrganization,
  updateOrganization,
} from "../api/Api";
import { showToast } from "../utils/toastUtils";
import { Header } from "./Home";
import styles from "./Organization.module.css";
import BreadCrumb from "../components/BreadCrumb";
import ShortTextInput from "../components/Inputs/ShortTextInput";
import { LuUsers } from "react-icons/lu";
import Box from "../components/ui/Box";
import { ImExit } from "react-icons/im";
import { Table } from "react-bootstrap"; // Bootstrap table
import CustomAvatar from "../components/ui/CustomAvatar";
import DeleteIcon from "../components/Inputs/DeleteIcon";
import { useFormik } from "formik";
import { organizationValidationSchema } from "../utils/validations";
import TextAreaInput from "../components/Inputs/TextAreaInput";
import DialogBox from "../components/modals/DialogBox";
import { ProjectContext } from "../contexts/ProjectContextProvider";

function Organization() {
  const { refresh: refreshProjects } = useContext(ProjectContext);
  const {
    data: organization,
    loading,
    refresh,
  } = useContext(OrganizationContext);
  const currentOrg = organization?.current_organization;
  const members = currentOrg?.members;
  const organization_id = currentOrg?.id;
  const [openAddMemberToOrgPopup, setOpenAddMemberToOrgPopup] = useState(false); // for invite modal
  const [showDeleteModel, setShowDeleteModel] = useState(false); // for delete modal
  const [leave, setLeave] = useState({
    open: false,
    user_id: null,
    processingLeave: false,
  }); // for leave organization alert

  // formik for organization details form
  const formik = useFormik({
    initialValues: {
      name: currentOrg?.name,
      description: currentOrg?.description,
    },
    enableReinitialize: true,
    validationSchema: organizationValidationSchema,
    onSubmit: async (values) => {
      console.log(values);
      const payload = {
        name: values.name,
        description: values.description,
      };

      updateOrganization(organization_id, payload)
        .then(() => {
          showToast("Organization updated successfully.", "success");
          refresh();
        })
        .catch(() => {
          showToast(
            "Failed to update organization. Please try again.",
            "error"
          );
        });
    },
  });

  // remove member from the organization
  const removeMember = async (user_id, organization_id) => {
    setLeave((pre) => ({ ...pre, processingLeave: true }));

    await removeMemberFromOrganization(organization_id, user_id)
      .then((response) => {
        if (response?.status === 200) {
          refresh();
          showToast("Member removed successfully", "success");
        } else if (response?.status === 202) {
          showToast(response?.message, "info");
        } else showToast(response.message, "error");
      })
      .catch((error) => {
        showToast(error?.message, "error");
      });
    setLeave((pre) => ({ ...pre, open: false, processingLeave: false }));
  };

  // delete organization
  const handleDeleteOrganization = async (organization_id) => {
    try {
      const response = await deleteOrganization(organization_id);
      if (response?.status === 200) {
        refresh();
        refreshProjects();
        showToast("Organization deleted successfully", "success");
      } else showToast("Personal organization cannot be deleted", "info");
    } catch (err) {
      console.log(err);
    }
    setShowDeleteModel(false);
  };

  const orgOwner =
    currentOrg?.owner?.first_name && currentOrg?.owner?.last_name
      ? `${currentOrg.owner.first_name} ${currentOrg.owner.last_name}`
      : "Not Available";
  //Fixed current user logic
  const selectedMember = members?.find((member) => member.id === leave.user_id);
  const isCurrentUser = selectedMember?.role?.toLowerCase().includes("(you)");

  if (loading) {
    return <CustomSkeleton count={3} />;
  }

  return (
    <Box>
      {/* breadcrumb */}
      <BreadCrumb
        array={[{ title: "Organization Settings" }]}
        icon={<IoSettingsOutline size={24} className={styles.headerIcon} />}
      />

      {/* organization details */}
      <Box className="bg-white ">
        <Box className="secondary-box rounded-4">
          <Box className="secondary-box rounded-4 bg-white">
            <form className="mt-3" onSubmit={formik.handleSubmit}>
              <div className="d-flex align-items-baseline">
                <p className="p3 " style={{ minWidth: "150px" }}>
                  Name
                </p>
                <ShortTextInput
                  id="name"
                  style={{ minWidth: "300px", maxwidth: "400px" }}
                  className="text-secondary"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  error={formik.errors.name}
                />
              </div>
              <div className="d-flex align-items-center">
                <p className="p3" style={{ minWidth: "150px" }}>
                  Owner
                </p>
                <ShortTextInput
                  style={{ minWidth: "300px", maxwidth: "400px" }}
                  className="text-secondary"
                  disabled
                  value={orgOwner}
                />
              </div>
              <div className="d-flex w-100">
                <p className="p3 mt-3" style={{ minWidth: "150px" }}>
                  Description
                </p>
                <TextAreaInput
                  id="description"
                  className="text-secondary"
                  onChange={formik.handleChange}
                  value={formik.values.description}
                  error={formik.errors.description}
                  fullWidth
                />
              </div>

              {/* buttons */}
              <div style={{ marginLeft: "150px" }} className="d-flex mt-3">
                <CustomButton
                  variant="outline"
                  className="px-5"
                  type="button"
                  onClick={formik.resetForm}
                >
                  Reset
                </CustomButton>
                <CustomButton
                  className="ms-2 px-5"
                  onClick={formik.handleSubmit}
                  type="submit"
                  disabled={formik.isSubmitting || !formik.isValid}
                >
                  Save
                </CustomButton>
              </div>
            </form>
          </Box>
        </Box>
      </Box>

      {/* member list */}
      <Box className="bg-white">
        <MemberListCard
          id={organization_id}
          members={members}
          loading={loading}
          onButtonClick={() => setOpenAddMemberToOrgPopup(true)}
          onDelete={(user_id) =>
            setLeave((pre) => ({ ...pre, open: true, user_id: user_id }))
          } // set leave alert
        />
      </Box>

      {/* delete card danger zone */}
      <Box className="bg-white">
        <DangerCard
          onClick={() => setShowDeleteModel(true)}
          bulletPoint2={
            <div>
              Deleting this{" "}
              <strong>Organization will permanently remove</strong> all
              associated data, including projects, workstreams, papers, and
              supplementary materials.
            </div>
          }
          buttonTitle="Delete Organization"
        />
      </Box>

      {/* invite modal */}
      <AddMembersToOrganization
        show={openAddMemberToOrgPopup}
        handleClose={() => setOpenAddMemberToOrgPopup(false)}
        organization_id={organization_id}
      />

      {/* delete organization model */}
      <DeleteOrganization
        show={showDeleteModel}
        handleClose={() => setShowDeleteModel(false)}
        handleDelete={() => handleDeleteOrganization(organization_id)}
        orgName={currentOrg?.name}
      />

      {/* leave/remove member project alert */}
      <DialogBox
        open={leave.open}
        setClose={() => setLeave((pre) => ({ ...pre, open: false }))}
        title={isCurrentUser ? "Leave Organization" : "Remove Member"}
        bodyText={
          isCurrentUser
            ? "Are you sure you want to leave this organization?"
            : "Are you sure you want to remove this member?"
        }
        submitButtonTitle="Yes"
        submitButtonVariant="btn-danger"
        onClick={() => removeMember(leave.user_id, organization_id)}
        loading={leave.processingLeave}
      />
    </Box>
  );
}

export default Organization;

// Modal for delete organization and get confirmation
const DeleteOrganization = ({ show, handleClose, handleDelete, orgName }) => {
  const [name, setName] = useState("");
  const disabled = String(name || "").trim() !== String(orgName || "").trim();

  console.log(orgName, name);

  return (
    <ModalUI show={show} handleClose={handleClose} title="Are You Sure?">
      <div className="p4 text-center p-2">
        Once deleted, this{" "}
        <strong>Organization will be permanently removed</strong> and cannot be
        recovered. Are you sure you want to delete?
      </div>
      <div className="my-1">
        <ShortTextInput
          id="orgName"
          label={
            <span style={{ textTransform: "none" }}>
              To confirm, type <strong>"{orgName}"</strong> in the box below
            </span>
          }
          value={name}
          onChange={(e) => {
            setName(e.target.value);
          }}
        />
      </div>
      <div className="d-flex mt-4">
        <CustomButton
          variant="outline"
          className="me-2 w-100"
          onClick={handleClose}
        >
          Cancel
        </CustomButton>
        <CustomButton
          disabled={disabled}
          className="w-100"
          onClick={handleDelete}
        >
          Delete
        </CustomButton>
      </div>
    </ModalUI>
  );
};

// Member List Card for list of members in the organization
export const MemberListCard = ({
  id,
  members,
  loading,
  onButtonClick,
  headerTitle = "Organization Members",
  headerButtonTitle = "Invite",
  onDelete,
}) => {
  // sort members to show logged in user at the top
  const sortedMembers = [...(members || [])].sort(
    (a, b) =>
      (b.role?.toLowerCase().includes("(you)") ? 1 : 0) -
      (a.role?.toLowerCase().includes("(you)") ? 1 : 0)
  );
  return (
    <div key={id}>
      <Header
        title={headerTitle}
        buttonTitle={headerButtonTitle}
        icon={<LuUsers size={28} />}
        buttonIcon={<FiPlus />}
        onButtonClick={onButtonClick}
      />
      <Box
        className="primary-box rounded-bottom-4"
        innerClass="overflow-hidden primary-box"
      >
        <MemberTable members={sortedMembers} onDelete={onDelete} />
      </Box>
    </div>
  );
};

// members table
const MemberTable = ({ members, onDelete }) => {
  const formatDate = (date) => {
    const day = new Date(date);
    const formattedDate = day
      .toLocaleDateString("en-US", {
        month: "short", // "Feb"
        day: "2-digit", // "26"
        year: "numeric", // "2025"
      })
      .replace(",", ""); // Removes comma;
    return formattedDate;
  };

  return (
    <Table className="mb-2">
      <thead>
        <tr className={styles.tableHeader}>
          <th
            className="p4 fw-semibold"
            style={{ minWidth: "200px", paddingLeft: "60px" }}
          >
            Name
          </th>
          <th className="text-center p4 fw-semibold">Role</th>
          <th className="text-center p4 fw-semibold">Joined</th>
          <th className="text-end p4 fw-semibold">Actions</th>
        </tr>
      </thead>
      <tbody className={styles.tableBody}>
        {members?.map((member, index) => {
          // FIXED: Determine if this member is the current user based on role
          const isCurrentUserInRow = member.role
            ?.toLowerCase()
            .includes("(you)");

          return (
            <tr key={member.id}>
              {/* Member Info */}
              <td className="d-flex align-items-center gap-3 p-3 ps-4">
                <CustomAvatar
                  user={{ first_name: member.name }}
                  size={30}
                  fontSize={20}
                />
                <div>
                  <div className="p4 fw-semibold">{member.name}</div>
                  <div className="p4">{member.email}</div>
                </div>
              </td>

              {/* Role */}
              <td className="text-center align-middle p4">{member.role}</td>

              {/* Created At */}
              <td className="text-center align-middle p4">
                {formatDate(member.joined_at)}
              </td>

              {/* Delete Button */}
              <td className="text-center align-middle align-items-end">
                <div className="d-flex justify-content-end pe-5">
                  {isCurrentUserInRow ? (
                    // Show exit icon for current user (leaving)
                    <ImExit
                      onClick={() => onDelete(member.id)}
                      color="gray"
                      size={18}
                      style={{ cursor: "pointer" }}
                      title="Leave Organization"
                    />
                  ) : (
                    // Show delete icon for other members
                    <DeleteIcon
                      id={member.id}
                      onDelete={() => onDelete(member.id)}
                      style={{ cursor: "pointer" }}
                      title="Remove Member"
                    />
                  )}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
